import { useWindowSize } from "@react-hookz/web";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, LinkButton, SearchInput } from "@/components/atoms";
import { useOperator, usePagination, useSearch, useSubscriptionAssignment } from "@/hooks";
import { getPlanLimits, shouldShowLimitIndicator } from "@/redux/apis/subscription/subscriptionLimits";
import { useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { clsx } from "@/utils";
import { VehicleFilters } from "../features/Filters/VehicleFilters";
import { VehicleLimitReachedModal } from "./modals/VehicleLimitReachedModal";

export const MyVehiclesActionMenu = () => {
  const { pathname } = useLocation();
  const [search, setSearch] = useSearch();
  const { clearPagination } = usePagination();
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const { id: operatorId } = useOperator();
  const { subscriptionStatus } = useSubscriptionAssignment();

  // Get current vehicle count
  const { data: vehiclesData } = useGetVehiclesQuery(
    {
      owner_uuid: operatorId ?? "",
      page_size: "1",
      page: "1",
    },
    {
      skip: !operatorId,
    }
  );

  const planSlug = subscriptionStatus.baseSubscription?.subscription.name.toLowerCase() ?? "";
  const limits = getPlanLimits(planSlug);
  const showLimitCheck = shouldShowLimitIndicator(planSlug);
  const vehicleCount = vehiclesData?.page.total ?? 0;
  const isAtVehicleLimit = showLimitCheck && vehicleCount >= limits.maxVehicles;

  const handleSearch = (value?: string) => {
    if (!value) setIsSearchActive(false);

    clearPagination();
    setSearch(value);
  };

  if (!isSearchActive && !search) {
    return (
      <>
        <div className="flex flex-1 items-center justify-end gap-0 xl:gap-2 ">
          <Button
            className="max-md:[&>span]:hidden"
            variant="tertiary"
            size="sm"
            startIcon="SearchNormal1"
            onClick={() => setIsSearchActive(true)}
          >
            Search
          </Button>
          <FilterButton />
          {isAtVehicleLimit ? (
            <Button className="max-md:[&>span]:hidden" variant="primary" size="sm" startIcon="Add" onClick={() => setShowLimitModal(true)}>
              Add Vehicle
            </Button>
          ) : (
            <LinkButton className="max-md:[&>span]:hidden" to={`${pathname}/add`} variant="primary" size="sm" startIcon="Add">
              Add Vehicle
            </LinkButton>
          )}
        </div>
        <VehicleLimitReachedModal isOpen={showLimitModal} onClose={() => setShowLimitModal(false)} limit={limits.maxVehicles} />
      </>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-end gap-2">
      <SearchInput
        search={search}
        onSearch={handleSearch}
        autoFocus
        placeholder="Search Number Plate, Make and Model"
        className={clsx("w-full max-w-sm p-0 sm:w-fit [&>input]:transition-size", isSearchActive && "[&>input]:w-[280px]")}
        size="sm"
        onClear={() => setIsSearchActive(false)}
      />
    </div>
  );
};

const FilterButton = () => {
  const { width } = useWindowSize();

  return width >= 768 ? <VehicleFilters.Button /> : <VehicleFilters.DrawerButton />;
};
